import type { VariantProps } from 'class-variance-authority'

import * as React from 'react'
import { cva } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '@/shared/lib/utils'

const badgeVariants = cva(
	'gap-1 rounded-4xl border border-transparent text-xs font-medium transition-all has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&>svg]:size-3! inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive overflow-hidden group/badge',
	{
		variants: {
			interactive: { true: 'cursor-pointer select-none hover:opacity-80' },
			variant: {
				default: 'bg-primary text-primary-foreground [a]:hover:bg-primary/80',
				secondary:
					'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
				destructive:
					'bg-destructive/10 [a]:hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 text-destructive dark:bg-destructive/20',
				outline:
					'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
				ghost:
					'hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50',
				link: 'text-primary underline-offset-4 hover:underline',

				neutral: 'bg-slate-200 dark:bg-neutral-700 [a]:hover:bg-neutral-700/80',
				warning:
					'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200',

				success:
					'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200',
				info: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-200',
				alert:
					'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200',
				epic: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
			},
			size: {
				default: 'h-5 px-2 py-0.5', //'h-6 min-w-6 px-2 py-1',
				icon: 'size-6 items-center justify-center p-0',
				sm: 'h-5 min-w-5 px-2 py-0.5 [&>svg]:size-3',
				'icon-sm': 'size-5 items-center justify-center p-0',
			},
		},
		defaultVariants: {
			variant: 'neutral',
			size: 'default',
		},
	}
)
type BadgeProps = React.ComponentProps<'span'> &
	VariantProps<typeof badgeVariants> & { asChild?: boolean }

function Badge({
	className,
	variant = 'neutral',
	asChild = false,
	interactive,
	size,
	...props
}: BadgeProps) {
	const Comp = asChild ? Slot.Root : 'span'

	return (
		<Comp
			data-slot='badge'
			data-variant={variant}
			data-size={size}
			className={cn(badgeVariants({ variant, size, interactive }), className)}
			{...props}
		/>
	)
}

export { Badge, badgeVariants, type BadgeProps }
